import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Dialog, Flex, Icon, Select, Text, TextInput, useToaster } from '@gravity-ui/uikit';
import { Plus, TrashBin } from '@gravity-ui/icons';
import {
  formatMoney,
  formatQuantity,
  lineTotal,
  parseDecimal,
  formatDate,
} from '@/features/warehouse/format';
import { QUANTITY_DECIMALS } from '@/features/warehouse/types';
import { errorText } from '@/shared/api/error-text';
import { useTranslation } from '@/shared/i18n';
import { useCreateBuffetSale, useCreateBuffetTransfer, useCreateBuffetWriteOff } from './api';
import type {
  BuffetAssortmentItem,
  BuffetDayRow,
  BuffetMovementKind,
  BuffetMovementLine,
} from './types';

interface DraftRow {
  id: number;
  productId: string;
  quantity: string;
}

/** Товар с остатком на день — для подписи в селекте и проверки количества. */
interface MovementOption {
  productId: string;
  name: string;
  unit: string;
  salePrice: number;
  costPrice: number;
  remaining: number;
}

function movementOptions(
  items: BuffetAssortmentItem[],
  rows: BuffetDayRow[],
  kind: BuffetMovementKind
): MovementOption[] {
  const byId = new Map(rows.map((row) => [row.productId, row]));

  return items
    .map((item) => {
      const row = byId.get(item.productId);
      return {
        productId: item.productId,
        name: item.productName,
        unit: item.unit,
        salePrice: row?.salePrice ?? item.salePrice,
        costPrice: row?.costPrice ?? 0,
        remaining: row?.closing ?? 0,
      };
    })
    .filter((option) => kind === 'transfer' || option.remaining > 0);
}

/**
 * Движение по буфету: приход со склада, продажа или списание.
 *
 * Один диалог на три операции: строки и проверки одинаковые, отличаются
 * заголовок, цена в сумме и обязательность причины списания.
 */
export function BuffetMovementDialog({
  open,
  kind,
  objectId,
  date,
  assortment,
  dayRows,
  demo,
  onClose,
}: {
  open: boolean;
  kind: BuffetMovementKind;
  objectId: string;
  date: string;
  assortment: BuffetAssortmentItem[];
  dayRows: BuffetDayRow[];
  /** Демо-режим: проводим движение локально, без запроса в бэкенд. */
  demo?: {
    onSubmit: (payload: {
      kind: BuffetMovementKind;
      items: BuffetMovementLine[];
      reason: string;
    }) => void;
  };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toaster = useToaster();
  const createTransfer = useCreateBuffetTransfer();
  const createSale = useCreateBuffetSale();
  const createWriteOff = useCreateBuffetWriteOff();
  const nextRowId = useRef(0);
  const createEmptyRow = () => ({
    id: nextRowId.current++,
    productId: '',
    quantity: '',
  });
  const [rows, setRows] = useState<DraftRow[]>(() => [createEmptyRow()]);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) return;

    setRows([createEmptyRow()]);
    setReason('');
  }, [open, kind]);

  const options = useMemo(
    () => movementOptions(assortment, dayRows, kind),
    [assortment, dayRows, kind]
  );
  const optionById = useMemo(
    () => new Map(options.map((option) => [option.productId, option])),
    [options]
  );

  const updateRow = (index: number, patch: Partial<DraftRow>) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const removeRow = (id: number) => {
    setRows((current) => current.filter((row) => row.id !== id));
  };

  const rowProblem = (row: DraftRow, index: number): string | null => {
    const option = optionById.get(row.productId);
    if (!option) return t('buffet.errorSelectProduct');

    if (rows.some((other, i) => i !== index && other.productId === row.productId)) {
      return t('buffet.errorDuplicateProduct');
    }

    const quantity = parseDecimal(row.quantity, QUANTITY_DECIMALS);
    if (quantity === null || quantity <= 0) return t('buffet.errorQuantity');

    if (kind !== 'transfer' && quantity > option.remaining) {
      return t('buffet.errorNotMore', {
        max: formatQuantity(option.remaining, option.unit),
      });
    }

    return null;
  };

  const problems = rows.map(rowProblem);
  const canSubmit =
    objectId !== '' &&
    rows.length > 0 &&
    (kind !== 'writeOff' || reason.trim() !== '') &&
    problems.every((problem) => problem === null);

  const total = rows.reduce((sum, row) => {
    const option = optionById.get(row.productId);
    if (!option) return sum;

    const quantity = parseDecimal(row.quantity, QUANTITY_DECIMALS) ?? 0;
    const price = kind === 'sale' ? option.salePrice : option.costPrice;

    return sum + lineTotal(quantity, price);
  }, 0);

  const kindTitle =
    kind === 'transfer'
      ? t('buffet.transferTitle')
      : kind === 'sale'
        ? t('buffet.saleTitle')
        : t('buffet.writeOffTitle');

  const submit = () => {
    const items = rows.map((row) => ({
      productId: row.productId,
      quantity: parseDecimal(row.quantity, QUANTITY_DECIMALS) ?? 0,
    }));

    const onSuccess = () => {
      toaster.add({
        name: `buffet-${kind}-created`,
        title:
          kind === 'transfer'
            ? t('buffet.transferSaved')
            : kind === 'sale'
              ? t('buffet.saleSaved')
              : t('buffet.writeOffSaved'),
        theme: 'success',
      });
      onClose();
    };
    const onError = (e: Error) =>
      toaster.add({
        name: `buffet-${kind}-error`,
        title: t('common.error'),
        content: errorText(e),
        theme: 'danger',
      });

    if (demo) {
      demo.onSubmit({ kind, items, reason: reason.trim() });
      onSuccess();
      return;
    }

    if (kind === 'transfer') {
      createTransfer.mutate({ objectId, date, items }, { onSuccess, onError });
    } else if (kind === 'sale') {
      createSale.mutate({ objectId, date, items }, { onSuccess, onError });
    } else {
      createWriteOff.mutate(
        { objectId, date, reason: reason.trim(), items },
        { onSuccess, onError }
      );
    }
  };

  const pending =
    !demo && (createTransfer.isPending || createSale.isPending || createWriteOff.isPending);

  return (
    <Dialog open={open} onClose={onClose} size="s">
      <Dialog.Header caption={`${kindTitle} · ${formatDate(date)}`} />
      <Dialog.Divider />
      <Dialog.Body>
        <Flex direction="column" gap={3}>
          {kind === 'writeOff' && (
            <TextInput
              size="l"
              value={reason}
              onUpdate={setReason}
              placeholder={t('buffet.reasonPlaceholder')}
              label={t('buffet.reason')}
            />
          )}

          {options.length === 0 && (
            <Text color="secondary">
              {kind === 'transfer' ? t('buffet.emptyAssortment') : t('buffet.emptyNoStock')}
            </Text>
          )}

          <Flex direction="column" gap={2}>
            {rows.map((row, index) => {
              return (
                <Flex key={row.id} className="buffet-movement-row" alignItems="flex-end" gap={2}>
                  <Select
                    size="l"
                    className="buffet-movement-product"
                    filterable
                    value={row.productId ? [row.productId] : []}
                    onUpdate={(next) => updateRow(index, { productId: next[0] ?? '' })}
                    options={options.map((item) => ({
                      value: item.productId,
                      content:
                        kind === 'transfer'
                          ? `${item.name} · ${item.unit}`
                          : t('buffet.productRemaining', {
                              name: item.name,
                              remaining: formatQuantity(item.remaining, item.unit),
                            }),
                    }))}
                    placeholder={t('buffet.productPlaceholder')}
                  />
                  <TextInput
                    size="l"
                    className="buffet-movement-quantity"
                    value={row.quantity}
                    onUpdate={(value) =>
                      updateRow(index, { quantity: value.replace(/[^\d.,]/g, '') })
                    }
                    placeholder={t('common.quantity')}
                    style={{ width: 110 }}
                  />
                  <Button
                    view="flat"
                    size="m"
                    onClick={() => removeRow(row.id)}
                    title={t('common.delete')}
                    aria-label={t('common.delete')}
                    className="buffet-movement-remove"
                  >
                    <Icon data={TrashBin} size={16} />
                  </Button>
                </Flex>
              );
            })}
          </Flex>

          <Button
            view="flat"
            size="m"
            onClick={() => {
              const row = createEmptyRow();
              setRows((current) => [...current, row]);
            }}
            style={{ alignSelf: 'flex-start' }}
          >
            <Icon data={Plus} size={16} />
            {t('buffet.addRow')}
          </Button>

          {rows.map((row, index) => {
            const touched = row.productId !== '' || row.quantity !== '';
            return touched && problems[index] ? (
              <Text key={row.id} color="danger">
                {problems[index]}
              </Text>
            ) : null;
          })}

          <Flex justifyContent="flex-end" gap={2}>
            <Text variant="body-2" color="secondary">
              {t('common.total')}:
            </Text>
            <Text variant="body-2">{formatMoney(total)}</Text>
          </Flex>
        </Flex>
      </Dialog.Body>
      <Dialog.Footer
        onClickButtonCancel={onClose}
        onClickButtonApply={submit}
        textButtonApply={t('buffet.applyMovement')}
        textButtonCancel={t('common.cancel')}
        propsButtonApply={{ loading: pending, disabled: !canSubmit || options.length === 0 }}
      />
    </Dialog>
  );
}
